// Rótulos y formato de la pestaña Pagos: el ledger que el motor de cobro asienta por crédito.
import type {
    CollectionMechanism,
    CollectionResultStatus,
    LoanPaymentListItem,
} from '@/api/services/loan-credit';
import { wireLookup } from '@/lib/wireLookup';
import type { CreditTone } from '../creditFile/logic';

const NO_MECHANISM_LABEL = '—';

export const COLLECTION_MECHANISM_LABEL: Record<CollectionMechanism, string> = {
    MXN_DIRECT: 'Cargo directo MXN',
    USD_DIRECT: 'Cargo directo USD',
    USD_PREAUTH: 'Preautorización USD',
};

/** `NONE` no es un fallo del cobro: es que no había con qué cobrar. */
export const COLLECTION_RESULT_PRESENTATION: Record<
    CollectionResultStatus,
    { label: string; tone: CreditTone }
> = {
    FULL: { label: 'Completo', tone: 'success' },
    PARTIAL: { label: 'Parcial', tone: 'warning' },
    NONE: { label: 'Sin cobro', tone: 'neutral' },
};

/** «Otro (CASH)»: el valor nuevo del backend se nombra en español y conserva el crudo para soporte. */
export function unknownWireLabel(value: string): string {
    return `Otro (${value})`;
}

/** Rótulo del mecanismo; uno nuevo sale como «Otro (valor)» y sin mecanismo (pago en tienda), «—». */
export function collectionMechanismLabelOf(mechanism: string | undefined): string {
    if (mechanism === undefined) return NO_MECHANISM_LABEL;
    return wireLookup(COLLECTION_MECHANISM_LABEL, mechanism) ?? unknownWireLabel(mechanism);
}

/** El resultado del cobro; uno nuevo del backend sale como «Otro (valor)» y neutro. */
export function collectionResultPresentationOf(result: string): {
    label: string;
    tone: CreditTone;
} {
    return (
        wireLookup(COLLECTION_RESULT_PRESENTATION, result) ?? {
            label: unknownWireLabel(result),
            tone: 'neutral',
        }
    );
}

/** Centavos en la moneda del pago; si Intl no reconoce el código, cae a «10.00 XX». */
export function formatPaymentCents(cents: number, currency: string): string {
    try {
        return new Intl.NumberFormat('es-MX', { style: 'currency', currency }).format(cents / 100);
    } catch {
        return `${(cents / 100).toFixed(2)} ${currency}`;
    }
}

type ChargeKind = NonNullable<LoanPaymentListItem['chargeKind']>;

export const CHARGE_KIND_LABEL: Record<ChargeKind, string> = {
    INSTALLMENT: 'Cuota',
    ADVANCE: 'Adelanto',
    PREPAYMENT: 'Abono a capital',
    SETTLEMENT: 'Liquidación',
    RECOVERY: 'Recuperación',
};

/**
 * Qué cobró el pago: «Cuota #3», «Adelanto · cuota #4», «Abono a capital». Un pago viejo sin tipo se
 * nombra por su cuota; sin cuota, «Pago».
 */
export function paymentKindLabelOf(
    payment: Pick<LoanPaymentListItem, 'chargeKind' | 'installmentNumber'>,
): string {
    const installment = payment.installmentNumber > 0 ? payment.installmentNumber : null;
    if (payment.chargeKind === undefined) {
        return installment === null ? 'Pago' : `Cuota #${String(installment)}`;
    }
    const label =
        wireLookup(CHARGE_KIND_LABEL, payment.chargeKind) ?? unknownWireLabel(payment.chargeKind);
    if (installment === null) return label;
    if (payment.chargeKind === 'INSTALLMENT') return `${label} #${String(installment)}`;
    if (payment.chargeKind === 'ADVANCE') return `${label} · cuota #${String(installment)}`;
    return label;
}

/** «Cupón #4», «Cupones #6 y #7», «Cupones #6 a #9» o «Cupones #1, #3 y #5»; vacía, null. */
export function couponListLabelOf(numbers: readonly number[]): string | null {
    const sorted = [...new Set(numbers)].sort((a, b) => a - b);
    const tags = sorted.map((n) => `#${String(n)}`);
    if (tags.length === 0) return null;
    if (tags.length === 1) return `Cupón ${tags[0]}`;
    const consecutive = sorted.every((n, i) => i === 0 || n === sorted[i - 1] + 1);
    if (tags.length > 2 && consecutive) return `Cupones ${tags[0]} a ${String(tags.at(-1))}`;
    return `Cupones ${tags.slice(0, -1).join(', ')} y ${String(tags.at(-1))}`;
}

/** Tipos que se nombran por los cupones que cubrieron; los demás, por su tipo. */
const COUPON_KINDS: ReadonlySet<string> = new Set(['INSTALLMENT', 'ADVANCE', 'RECOVERY']);

type CouponFacts = Pick<
    LoanPaymentListItem,
    'chargeKind' | 'installmentNumber' | 'installmentNumbers'
>;

/** Los cupones que cubrió el pago (su lista o, si no viene, su primera cuota); sin cupones, null. */
export function paymentCouponsOf(payment: CouponFacts): string | null {
    if (!COUPON_KINDS.has(payment.chargeKind ?? 'INSTALLMENT')) return null;
    const numbers = payment.installmentNumbers?.length
        ? payment.installmentNumbers
        : [payment.installmentNumber].filter((n) => n > 0);
    return couponListLabelOf(numbers);
}

/** Qué cubrió el pago: sus cupones; abono, liquidación o recuperación sin cuotas, su tipo. */
export function paymentCouponLabelOf(payment: CouponFacts): string {
    const coupons = paymentCouponsOf(payment);
    if (coupons !== null) return coupons;
    if (payment.chargeKind === undefined) return 'Pago';
    return (
        wireLookup(CHARGE_KIND_LABEL, payment.chargeKind) ?? unknownWireLabel(payment.chargeKind)
    );
}

/** «$3,100.00»; con monedas mezcladas, una suma por moneda. */
export function appliedTotalOf(
    rows: readonly Pick<LoanPaymentListItem, 'appliedCents' | 'currency'>[],
): string {
    const byCurrency = new Map<string, number>();
    for (const row of rows) {
        byCurrency.set(row.currency, (byCurrency.get(row.currency) ?? 0) + row.appliedCents);
    }
    return [...byCurrency.entries()]
        .map(([currency, cents]) => formatPaymentCents(cents, currency))
        .join(' + ');
}
